import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { RANGES } from "../../widgets/charts/series";
import type { Widget, WidgetInput } from "../../widgets/types";
import { PageWidget } from "../devices/page/PageWidget";
import { cellStyle } from "../devices/page/WidgetGrid";
import type { PageDocument } from "../devices/page/profile";
import type { WidgetPage } from "../devices/page/queries";
import { AddWidgetDialog, type LineType } from "./AddWidgetDialog";
import { imageConfig } from "./images";
import { NoteDialog, PictureDialog } from "./pictures/PictureDialogs";
import type { DashboardDocument } from "./copy";
import { MAX_WIDGETS } from "./copy";
import {
  MULTI_VALUE,
  change,
  layoutProblems,
  move,
  normalize,
  remove,
  removeValue,
  resize,
  setSize,
  sizePreset,
  step,
  swap,
  newWidget,
  type DocWidget,
} from "./layout";

const ROW_PX = 72;
const GAP_PX = 12;
const EMPTY_ROWS = 3;
const CHARTS = new Set(["line_chart", "bar_chart"]);
const SIZES = ["small", "medium", "large", "full"] as const;

function without<T extends object>(o: T, key: string): T {
  return Object.fromEntries(Object.entries(o).filter(([k]) => k !== key)) as T;
}

/**
 * Edit mode (WEB-DSH-EDIT-01, DDB-30 … DDB-34): add from the picker; move by dragging the widget itself, resize by
 * dragging its corner, or use the keyboard on the focused grip (arrows move, Shift+arrows resize); the ⋯ menu has
 * Move up and down, Rename, Change (another value or another way to show it), Size, the Values of a widget that shows
 * several, and a chart's range; ✕ removes. Gravity closes every gap. Nothing is saved until Save; Cancel drops the
 * draft.
 */
export function DashboardEditor({
  doc,
  device,
  orgId,
  input,
  inputFor,
  now,
  saving,
  errors,
  notice,
  onSave,
  onCancel,
  source,
  previewNote,
  saveLabel,
  refused,
  missing,
  onDraft,
}: {
  doc: DashboardDocument;
  /** The device page's editor: widgets are added for this device only. */
  device?: string;
  orgId: string;
  input: WidgetInput;
  inputFor: (w: Widget) => WidgetInput;
  now: Date;
  saving: boolean;
  errors?: string[];
  /** A line shown on a saved widget, e.g. that its type no longer offers this way of showing a value (DTC-22). */
  notice?: (w: Widget) => string | undefined;
  onSave: (doc: DashboardDocument) => void;
  onCancel: () => void;
  /** The device's page and profile for the picker, when not read from an organisation (WEB-DTP-03). */
  source?: { page: WidgetPage; doc: PageDocument };
  /** A line above the grid, e.g. that the values are samples (WEB-DTP-04). */
  previewNote?: string;
  /** Save's words when it does not save on the server, e.g. "Done" (WEB-DTP-02). */
  saveLabel?: string;
  /** The widgets a refused save named, by index in the document sent (refusedWidgets): marked on the grid (#9). */
  refused?: number[];
  /** Devices whose page the API refuses (deleted, or no access): their widgets are marked on the grid (#9). */
  missing?: ReadonlySet<string>;
  /** Told the draft as it changes, and undefined when the editor closes, so the page reads its values (#6). */
  onDraft?: (widgets: Widget[] | undefined) => void;
}) {
  const { t } = useTranslation();
  // Older documents may have gaps or widgets below their type's minimum: the editor starts from, and saves, the
  // normalised layout (DDB-30, DDB-32).
  const [draft, setDraft] = useState<DocWidget[]>(() => normalize(doc.widgets));
  const [menu, setMenu] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<string>();
  const [lineFor, setLineFor] = useState<string>();
  const [changing, setChanging] = useState<string>();
  // A note or a picture being added (no id) or changed (its id): they bind no device (dashboard-images IMG-08).
  const [note, setNote] = useState<{ id?: string }>();
  const [picture, setPicture] = useState<{ id?: string }>();
  // Values added in this session: the input only knows the saved document's points until Save.
  const [added, setAdded] = useState<Record<string, { unit: string | null; name: string }>>({});
  const [announce, setAnnounce] = useState("");
  const grid = useRef<HTMLDivElement>(null);
  const drag = useRef<
    { id: string; x: number; y: number; start: DocWidget[]; resize: boolean; moved: boolean } | undefined
  >(undefined);
  const [dragging, setDragging] = useState<string>();
  // The draft as last sent to Save: a refusal names its widgets by their index in it.
  const [sent, setSent] = useState<DocWidget[]>([]);
  useEffect(() => onDraft?.(draft as unknown as Widget[]), [draft, onDraft]);
  useEffect(() => () => onDraft?.(undefined), [onDraft]);
  const refusedIds = new Set((refused ?? []).flatMap((i) => (sent[i] ? [sent[i].id] : [])));
  const broken = (w: DocWidget) =>
    refusedIds.has(w.id) || (w.bindings ?? []).some((b) => missing?.has(b.device) ?? false);

  const ordered = [...draft].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
  const problems = layoutProblems(draft);
  const bottom = Math.max(0, ...draft.map((w) => w.position.y + w.position.h));
  const nameOf = (w: DocWidget) => {
    if (w.title) return w.title;
    const b = w.bindings?.find((x) => x.datapoint);
    const point = b?.datapoint ? input.points?.[`${b.device}/${b.datapoint}`] : undefined;
    return point?.name ?? t(`editor.type.${w.type}`);
  };
  const update = (next: DocWidget[], id: string) => {
    setDraft(next);
    const w = next.find((x) => x.id === id);
    if (w) setAnnounce(t("editor.at", { name: nameOf(w), ...w.position, x: w.position.x + 1, y: w.position.y + 1 }));
  };

  function onKey(e: KeyboardEvent, id: string) {
    if (e.key === "Escape" && drag.current) {
      setDraft(drag.current.start);
      drag.current = undefined;
      setDragging(undefined);
      return;
    }
    const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (!d) return;
    e.preventDefault();
    const [dx, dy] = d as [number, number];
    update(e.shiftKey ? resize(draft, id, dx, dy) : step(draft, id, dx, dy), id);
  }
  function onPointerDown(e: PointerEvent, id: string, resizing = false) {
    // Buttons and fields inside the widget keep their own clicks; the grip and the corner start a drag.
    const target = e.target as HTMLElement;
    if (!resizing && target.closest("button:not([data-grip]),input,select,label,[role=menu]")) return;
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id, x: e.clientX, y: e.clientY, start: draft, resize: resizing, moved: false };
    setMenu(undefined);
  }
  function onPointerMove(e: PointerEvent) {
    const g = drag.current;
    const el = grid.current;
    if (!g || !el) return;
    const col = (el.clientWidth + GAP_PX) / 12;
    const dx = Math.round((e.clientX - g.x) / col);
    const dy = Math.round((e.clientY - g.y) / (ROW_PX + GAP_PX));
    if (!g.moved && dx === 0 && dy === 0) return;
    g.moved = true;
    setDragging(g.id);
    const from = g.start.find((w) => w.id === g.id)?.position;
    if (!from) return;
    setDraft(g.resize ? setSize(g.start, g.id, { w: from.w + dx, h: from.h + dy }) : move(g.start, g.id, dx, dy));
  }
  function onPointerUp() {
    const g = drag.current;
    drag.current = undefined;
    setDragging(undefined);
    if (g?.moved) update(draft, g.id);
  }
  const lineChart = draft.find((w) => w.id === lineFor);
  const lineOf = (w: DocWidget) => {
    const bound = (w.bindings ?? []).flatMap((b) => (b.datapoint ? [`${b.device}/${b.datapoint}`] : []));
    const points = inputFor(w as unknown as Widget).points ?? {};
    return {
      type: w.type as LineType,
      // The units the chart draws: its first two, as HistoryChart does.
      units: [...new Set(bound.map((k) => (k in added ? added[k]?.unit : points[k]?.unit) ?? null))].slice(0, 2),
      taken: bound,
    };
  };
  // A value in the Values list: its name, after its device's when the widget shows several devices.
  const valuesOf = (w: DocWidget) => {
    const input = inputFor(w as unknown as Widget);
    const bindings = w.bindings ?? [];
    const many = new Set(bindings.map((b) => b.device)).size > 1;
    return bindings.map((b) => {
      const k = `${b.device}/${b.datapoint}`;
      const name = added[k]?.name ?? input.points?.[k]?.name ?? b.datapoint ?? "";
      const dev = input.devices?.[b.device]?.name;
      return many && dev ? `${dev} · ${name}` : name;
    });
  };
  const changed = draft.find((w) => w.id === changing);
  const setWidget = (id: string, patch: (w: DocWidget) => DocWidget) =>
    setDraft(draft.map((w) => (w.id === id ? patch(w) : w)));

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" disabled={draft.length >= MAX_WIDGETS} onClick={() => setAdding(true)}>
          {t("editor.addWidget")}
        </Button>
        {/* Notes and pictures are the organisation's, so a dashboard has them and a device page does not. */}
        {!device && !source && (
          <>
            <Button variant="secondary" disabled={draft.length >= MAX_WIDGETS} onClick={() => setNote({})}>
              {t("images.note.open")}
            </Button>
            <Button variant="secondary" disabled={draft.length >= MAX_WIDGETS} onClick={() => setPicture({})}>
              {t("images.picture.open")}
            </Button>
          </>
        )}
        <span className="text-xs text-gray-700">{t("editor.keys")}</span>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={saving || problems.length > 0}
            onClick={() => {
              setSent(draft);
              onSave({ ...doc, widgets: draft });
            }}
          >
            {saveLabel ?? t("dashboards.save")}
          </Button>
        </div>
      </div>
      {previewNote && <p className="mt-2 text-sm text-gray-800">{previewNote}</p>}
      {problems.length > 0 && (
        <p role="alert" className="mt-2 text-sm text-red-900">
          {t("editor.layoutProblem")}
        </p>
      )}
      {errors && errors.length > 0 && (
        <ul role="alert" className="mt-2 list-disc pl-5 text-sm text-red-900">
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
      {draft.length === 0 && (
        <p className="mt-6 rounded-md border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-800">
          {t("editor.empty")}
        </p>
      )}
      <div
        ref={grid}
        role="list"
        aria-label={t("editor.label", { name: doc.name })}
        className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-12"
        // Fixed rows, so a widget is drawn at exactly its size: Shorter and Taller always show, and a drag's rows
        // match the pointer.
        // Three empty rows under the last widget, so one can be dragged below it (owner's report #8).
        style={{ gridAutoRows: `${ROW_PX}px`, minHeight: `${(bottom + EMPTY_ROWS) * (ROW_PX + GAP_PX)}px` }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {ordered.map((w) => {
          const name = nameOf(w);
          const asWidget = w as unknown as Widget;
          const bad = broken(w);
          return (
            <div
              key={w.id}
              role="listitem"
              data-edit-cell={w.id}
              data-broken={bad || undefined}
              style={cellStyle(asWidget)}
              onPointerDown={(e) => onPointerDown(e, w.id)}
              className={`relative flex min-w-0 flex-col gap-1 rounded-xl border bg-white select-none md:touch-none md:[grid-column:var(--gc)] md:[grid-row:var(--gr)] ${
                dragging === w.id
                  ? "z-10 cursor-grabbing border-emerald-700 shadow-xl ring-2 ring-emerald-700/30"
                  : bad
                    ? "cursor-grab border-red-700 ring-2 ring-red-700/30"
                    : "cursor-grab border-gray-300 hover:border-gray-400"
              }`}
            >
              <div className="flex items-center gap-1 px-1 pt-1 text-xs">
                <button
                  type="button"
                  data-grip
                  aria-label={t("editor.move", { name })}
                  onKeyDown={(e) => onKey(e, w.id)}
                  className="min-h-6 min-w-0 cursor-grab truncate rounded px-2 py-1 font-medium text-gray-900 hover:bg-gray-100"
                >
                  <span aria-hidden="true" className="mr-1 text-gray-500">
                    ⠿
                  </span>
                  {name}
                </button>
                <span className="ml-auto flex gap-1">
                  <button
                    type="button"
                    aria-label={t("editor.more", { name })}
                    aria-haspopup="menu"
                    aria-expanded={menu === w.id}
                    onClick={() => setMenu(menu === w.id ? undefined : w.id)}
                    className="grid size-7 place-items-center rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200"
                  >
                    ⋯
                  </button>
                  <button
                    type="button"
                    aria-label={t("editor.removeNamed", { name })}
                    onClick={() => setDraft(remove(draft, w.id))}
                    className="grid size-7 place-items-center rounded-full bg-gray-100 text-gray-800 hover:bg-red-50 hover:text-red-800"
                  >
                    ✕
                  </button>
                </span>
              </div>
              {menu === w.id && (
                <div
                  role="menu"
                  aria-label={t("editor.more", { name })}
                  onKeyDown={(e) => e.key === "Escape" && setMenu(undefined)}
                  className="absolute top-9 right-1 z-20 flex w-52 flex-col rounded-lg border border-gray-200 bg-white p-1 text-sm shadow-lg"
                >
                  {(
                    [
                      ["moveUp", -1],
                      ["moveDown", 1],
                    ] as const
                  ).map(([k, dir]) => (
                    <button
                      key={k}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        update(swap(draft, w.id, dir), w.id);
                        setMenu(undefined);
                      }}
                      className="rounded px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100"
                    >
                      {t(`editor.${k}`)}
                    </button>
                  ))}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setRenaming(w.id);
                      setMenu(undefined);
                    }}
                    className="rounded px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100"
                  >
                    {t("editor.rename")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      if (w.type === "text") setNote({ id: w.id });
                      else if (w.type === "image") setPicture({ id: w.id });
                      else setChanging(w.id);
                      setMenu(undefined);
                    }}
                    className="rounded px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100"
                  >
                    {t("editor.change")}
                  </button>
                  <p className="px-2 pt-2 pb-1 text-xs text-gray-700">{t("editor.size")}</p>
                  <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                    {SIZES.map((k) => (
                      <button
                        key={k}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          update(setSize(draft, w.id, sizePreset(w.type, k)), w.id);
                          setMenu(undefined);
                        }}
                        className="rounded border border-gray-200 px-2 py-1 text-gray-900 hover:bg-gray-100"
                      >
                        {t(`editor.sizes.${k}`)}
                      </button>
                    ))}
                  </div>
                  {w.type in MULTI_VALUE && (
                    <>
                      <p aria-hidden="true" className="px-2 pt-2 pb-1 text-xs text-gray-700">
                        {t("editor.values")}
                      </p>
                      <div role="group" aria-label={t("editor.values")} className="px-1">
                        {valuesOf(w).map((value, i) => (
                          <div key={i} className="flex items-center gap-1 rounded px-1 py-0.5 text-gray-900">
                            <span className="min-w-0 flex-1 truncate">{value}</span>
                            <button
                              type="button"
                              role="menuitem"
                              aria-label={t("editor.removeValue", { value, name })}
                              disabled={(w.bindings?.length ?? 0) < 2}
                              onClick={() => setDraft(removeValue(draft, w.id, i))}
                              className="grid size-6 place-items-center rounded-full text-gray-800 hover:bg-red-50 hover:text-red-800 disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      {(w.bindings?.length ?? 0) < (MULTI_VALUE[w.type] ?? 0) && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setLineFor(w.id);
                            setMenu(undefined);
                          }}
                          className="rounded px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100"
                        >
                          {t(CHARTS.has(w.type) ? "editor.addLine" : "editor.addValue")}
                        </button>
                      )}
                    </>
                  )}
                  {CHARTS.has(w.type) && (
                    <label className="flex items-center gap-2 px-2 py-1.5 text-gray-900">
                      {t("widgets.chart.range")}
                      <select
                        value={
                          typeof (w.config as { range?: unknown } | undefined)?.range === "string"
                            ? String((w.config as { range: string }).range)
                            : ""
                        }
                        onChange={(e) =>
                          setWidget(w.id, (x) => {
                            const config = without((x.config ?? {}) as Record<string, unknown>, "range");
                            return { ...x, config: e.target.value ? { ...config, range: e.target.value } : config };
                          })
                        }
                        className="ml-auto rounded border border-gray-300 px-1 py-0.5"
                      >
                        <option value="">{doc.time_range ?? "24h"}</option>
                        {RANGES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}
              {renaming === w.id && (
                <label className="flex items-center gap-2 px-2 text-xs text-gray-900">
                  {t("editor.title")}
                  <input
                    autoFocus
                    defaultValue={w.title ?? ""}
                    maxLength={100}
                    // Enter keeps the title too: closing the field unmounts it, and an unmounted input gets no blur.
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") setRenaming(undefined);
                    }}
                    onBlur={(e) => {
                      const title = e.target.value.trim();
                      setWidget(w.id, (x) => (title ? { ...x, title } : without(x, "title")));
                      setRenaming(undefined);
                    }}
                    className="flex-1 rounded border border-gray-300 px-2 py-1"
                  />
                </label>
              )}
              {notice?.(asWidget) && (
                <p className="mx-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">{notice(asWidget)}</p>
              )}
              {bad && (
                <p className="mx-2 rounded bg-red-50 px-2 py-1 text-xs text-red-900">{t("editor.broken", { name })}</p>
              )}
              {/* Rows are fixed here, so content is kept inside the frame as it is when saved (owner's report #7). */}
              <div className="pointer-events-none min-h-0 flex-1 overflow-hidden rounded-b-xl" inert>
                <PageWidget widget={asWidget} input={inputFor(asWidget)} now={now} orgId={orgId} preview />
              </div>
              {/* The open menu covers the corner, so the handle waits until it closes. */}
              {menu !== w.id && (
                <button
                  type="button"
                  aria-label={t("editor.resize", { name })}
                  onKeyDown={(e) => {
                    const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
                    if (!d) return onKey(e, w.id);
                    e.preventDefault();
                    const [dw, dh] = d as [number, number];
                    update(resize(draft, w.id, dw, dh), w.id);
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    onPointerDown(e, w.id, true);
                  }}
                  className="absolute right-0 bottom-0 size-6 cursor-nwse-resize touch-none rounded-br-xl border-r-[3px] border-b-[3px] border-emerald-800/60 hover:border-emerald-800"
                />
              )}
            </div>
          );
        })}
      </div>
      {/* Mounted while open, so each starts from the widget it changes. */}
      {note && (
        <NoteDialog
          open
          initial={(() => {
            const c = draft.find((w) => w.id === note?.id)?.config as { text?: unknown } | undefined;
            return typeof c?.text === "string" ? c.text : undefined;
          })()}
          onDone={(config) => {
            if (note?.id) setWidget(note.id, (x) => ({ ...x, config }));
            else setDraft((d) => [...d, newWidget(d, { type: "text", config }, "")]);
          }}
          onClose={() => setNote(undefined)}
        />
      )}
      {picture && (
        <PictureDialog
          open
          orgId={orgId}
          initial={imageConfig(draft.find((w) => w.id === picture?.id)?.config as Record<string, unknown> | undefined)}
          onDone={(config) => {
            if (picture?.id) setWidget(picture.id, (x) => ({ ...x, config }));
            else setDraft((d) => [...d, newWidget(d, { type: "image", config }, "")]);
          }}
          onClose={() => setPicture(undefined)}
        />
      )}
      <AddWidgetDialog
        open={adding}
        orgId={orgId}
        source={source}
        widgets={draft}
        device={device}
        onAdd={(w) => setDraft((d) => [...d, w])}
        onClose={() => setAdding(false)}
      />
      {/* A chart on a dashboard gains a line from any device (DDB-36); a device page's charts stay on its device. */}
      <AddWidgetDialog
        open={!!lineChart}
        orgId={orgId}
        source={source}
        widgets={draft}
        // A device tile shows one device (DTP-13): its values come from that device.
        device={lineChart?.type === "device_tile" ? (lineChart.bindings?.[0]?.device ?? device) : device}
        line={lineChart && lineOf(lineChart)}
        onAdd={() => undefined}
        onAddLine={(b, unit, name) => {
          if (!lineChart) return;
          setAdded((a) => ({ ...a, [`${b.device}/${b.datapoint}`]: { unit, name } }));
          setWidget(lineChart.id, (x) => ({ ...x, bindings: [...(x.bindings ?? []), b] as DocWidget["bindings"] }));
        }}
        onClose={() => setLineFor(undefined)}
      />
      <AddWidgetDialog
        open={!!changed}
        orgId={orgId}
        source={source}
        widgets={draft}
        device={device}
        change={changed}
        unitOf={(k) => {
          const points = changed ? (inputFor(changed as unknown as Widget).points ?? {}) : {};
          return (k in added ? added[k]?.unit : points[k]?.unit) ?? null;
        }}
        onAdd={() => undefined}
        onChange={(built, dev) => changed && update(change(draft, changed.id, built, dev), changed.id)}
        onClose={() => setChanging(undefined)}
      />
    </div>
  );
}
